/**
 * Deck Pack remote relay HTTP client (Cloudflare Pages `/remote-api/v1`).
 */

export function getRemoteApiBase() {
  const raw = process.env.REACT_APP_REMOTE_API_URL;
  if (raw && typeof raw === 'string' && raw.trim()) {
    const s = raw.trim().replace(/\/$/, '');
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    const path = s.startsWith('/') ? s : `/${s}`;
    return `${window.location.origin}${path}`;
  }
  return `${window.location.origin}/remote-api/v1`;
}

async function apiFetch(path, options = {}) {
  const url = `${getRemoteApiBase()}${path.startsWith('/') ? path : `/${path}`}`;
  const headers = { ...(options.headers || {}) };
  if (options.body != null && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const method = String(options.method || 'GET').toUpperCase();
  const res = await fetch(url, {
    ...options,
    headers,
    // Avoid stale pending/display counts (browser or CDN); critical for join notifications.
    cache: options.cache ?? (method === 'GET' ? 'no-store' : undefined),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error_description || data.error || `Request failed (${res.status})`;
    const err = new Error(msg);
    if (data.error) err.code = data.error;
    throw err;
  }
  return data;
}

export function remoteCreateRoom() {
  return apiFetch('/rooms', { method: 'POST', body: '{}' });
}

export function remoteGetRoom(roomId, operatorSecret) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}`, {
    method: 'GET',
    headers: { 'X-Deckpack-Operator': operatorSecret },
  });
}

export function remoteJoinRoom(roomId, name, device) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/join`, {
    method: 'POST',
    body: JSON.stringify({ name: name.trim(), device: (device || '').trim() || undefined }),
  });
}

export function remotePollMe(roomId, clientId) {
  const q = new URLSearchParams({ clientId });
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/me?${q}`, { method: 'GET' });
}

export function remoteGetDisplay(roomId, remoteToken) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/display`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${remoteToken}` },
  });
}

export function remotePostCommand(roomId, remoteToken, action) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/remote-command`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${remoteToken}` },
    body: JSON.stringify({ action }),
  });
}

export function remotePushState(roomId, operatorSecret, display) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/state`, {
    method: 'POST',
    headers: { 'X-Deckpack-Operator': operatorSecret },
    body: JSON.stringify(display),
  });
}

export function remotePollInbox(roomId, operatorSecret, after) {
  const q = new URLSearchParams({ after: String(after ?? 0) });
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/inbox?${q}`, {
    method: 'GET',
    headers: { 'X-Deckpack-Operator': operatorSecret },
  });
}

export function remoteAckInbox(roomId, operatorSecret, upToId) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/inbox/ack`, {
    method: 'POST',
    headers: { 'X-Deckpack-Operator': operatorSecret },
    body: JSON.stringify({ upToId }),
  });
}

export function remoteApprove(roomId, operatorSecret, clientId) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/approve`, {
    method: 'POST',
    headers: { 'X-Deckpack-Operator': operatorSecret },
    body: JSON.stringify({ clientId }),
  });
}

export function remoteDeny(roomId, operatorSecret, clientId) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/deny`, {
    method: 'POST',
    headers: { 'X-Deckpack-Operator': operatorSecret },
    body: JSON.stringify({ clientId }),
  });
}

export function remoteKick(roomId, operatorSecret, clientId) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/kick`, {
    method: 'POST',
    headers: { 'X-Deckpack-Operator': operatorSecret },
    body: JSON.stringify({ clientId }),
  });
}

export function remoteBlock(roomId, operatorSecret, clientId) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/block`, {
    method: 'POST',
    headers: { 'X-Deckpack-Operator': operatorSecret },
    body: JSON.stringify({ clientId }),
  });
}

export function remoteUnblock(roomId, operatorSecret, clientId) {
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/unblock`, {
    method: 'POST',
    headers: { 'X-Deckpack-Operator': operatorSecret },
    body: JSON.stringify({ clientId }),
  });
}

export function remoteCloseRoom(roomId, operatorSecret) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 12000);
  return apiFetch(`/rooms/${encodeURIComponent(roomId)}/close`, {
    method: 'POST',
    headers: { 'X-Deckpack-Operator': operatorSecret },
    signal: ctrl.signal,
  }).finally(() => clearTimeout(t));
}
